import { useMemo, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  InputBase,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ConfigProvider, DatePicker, theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import { KeyboardArrowDownRounded as ChevronIcon } from '@mui/icons-material';
import { ChooseDevicesDialog } from './choose-devices-dialog';
import { ConfirmRunDialog } from './confirm-run-dialog';
import { inputSx, plainButtonSx, primaryButtonSx } from './styles';
import { useCreateShellJob, useShellJobPreview } from '@/hooks/use-shell-jobs';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useNow } from '@/hooks/use-now';
import { usePermissions } from '@/hooks/use-permissions';
import { useSoftwareUpdateGroups } from '@/hooks/use-software-update-groups';
import { useTenants } from '@/hooks/use-tenants';
import { useTimezone } from '@/hooks/use-timezone';
import type { ShellJobTargetRequest } from '@/lib/shell-job.service';
import type { BulkShellPrefill } from '@/lib/bulk-shell-prefill';
import { choiceLine, targetRequest, type DevicesChoice } from '@/lib/bulk-shell-target';
import {
  DEFAULT_SHELL_TIMEOUT_MS,
  DEFAULT_SLOT_RELEASE_MS,
  isCommandRisky,
  isLongRunning,
  NO_TIMEOUT,
  RUN_TIMEOUT_OPTIONS,
  SLOT_RELEASE_OPTIONS,
  slotReleaseLabel,
} from '@/lib/shell-command';
import { allSentIn, paceEstimate } from '@/lib/shell-job-status';
import { DEADLINE_OPTIONS, customDeadlineDayAllowed, formatDeadline, resolveDeadline, type DeadlineChoice } from '@/lib/run-deadline';
import { getAPIErrorMessage } from '@/lib/api';
import { BULK_SHELL_DRAWER_WIDTH, useUIStore } from '@/stores/ui.store';
import { SHELL_MONO, TERMINAL } from '@/theme/tokens';

const CONCURRENCY_OPTIONS = [5, 10, 20, 50];
const DEFAULT_CONCURRENCY = 20;
/** "Try 10% first, then wait for me". */
const TRY_FIRST = { wave_percents: [10, 100], pause_between: true };
/** How many devices the Preview link lists. */
/** Dragging the drawer's edge: no narrower than this, and always leave this much page showing. */
const MIN_WIDTH = 420;
const PAGE_GUTTER = 120;
const clampWidth = (w: number) => Math.round(Math.min(Math.max(w, MIN_WIDTH), Math.max(MIN_WIDTH, window.innerWidth - PAGE_GUTTER)));

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** One part of the form, ruled off from the next. */
function Section({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <Box sx={{ py: 2, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      {label && <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>{label}</Typography>}
      {children}
    </Box>
  );
}

function SmallLabel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Typography id={id} sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
      {children}
    </Typography>
  );
}

const Strong = ({ children }: { children: ReactNode }) => (
  <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
    {children}
  </Box>
);

const selectSx = { ...inputSx, '& .MuiSelect-select': { py: '6px', pl: '10px' } } as const;


const radioSx = { m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, color: 'text.secondary' } } as const;

interface NewRunDrawerProps {
  open: boolean;
  onClose: () => void;
  prefill: BulkShellPrefill;
}

/**
 * The New run form in a right-side drawer. It stays mounted while closed
 * (keepMounted), so closing and reopening keeps the draft. Only ✕ and Cancel
 * close it: with no onClose, a click outside or Esc cannot lose a typed command.
 */
export function NewRunDrawer({ open, onClose, prefill }: NewRunDrawerProps) {
  const navigate = useNavigate();
  const { isSuperAdmin, can } = usePermissions();
  const viewConfigs = can.viewConfigs;
  const groups = useSoftwareUpdateGroups({ enabled: viewConfigs });
  const tenantsQuery = useTenants({ enabled: isSuperAdmin });
  const { timezone } = useTimezone();
  const now = useNow(30_000);
  const createJob = useCreateShellJob();

  const [tenantId, setTenantId] = useState(prefill.tenant_id ?? '');
  // The devices the run targets; the Choose devices window edits a copy.
  // Filter starts by leaving internal devices out, as a customer-facing run
  // should; only super admins see (and send) that facet.
  const [choice, setChoice] = useState<DevicesChoice>(() => ({
    mode: prefill.target ?? 'all',
    filter: prefill.filter ?? (isSuperAdmin ? { is_internal: false } : {}),
    listText: (prefill.devices ?? []).join('\n'),
    groupId: prefill.group_id ?? '',
  }));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [command, setCommand] = useState(prefill.command ?? '');
  const [description, setDescription] = useState('');
  const [timeoutMs, setTimeoutMs] = useState(
    RUN_TIMEOUT_OPTIONS.find((o) => o.ms === prefill.timeout_ms)?.ms ?? DEFAULT_SHELL_TIMEOUT_MS,
  );
  // No timeout sends and forgets, a batch of `concurrency` devices every slot release.
  const noTimeout = timeoutMs === NO_TIMEOUT;
  const [slotReleaseMs, setSlotReleaseMs] = useState(
    SLOT_RELEASE_OPTIONS.find((o) => o.ms === prefill.slot_release_ms)?.ms ?? DEFAULT_SLOT_RELEASE_MS,
  );
  const [concurrency, setConcurrency] = useState(
    prefill.concurrency && CONCURRENCY_OPTIONS.includes(prefill.concurrency) ? prefill.concurrency : DEFAULT_CONCURRENCY,
  );
  const [deadline, setDeadline] = useState<DeadlineChoice>('none');
  const groupName = groups.data?.find((g) => g.id === choice.groupId)?.name;
  const joinUntil = deadline !== 'none' ? 'until the deadline' : 'while the run is still sending';
  const joinHint =
    choice.mode === 'list'
      ? 'Only the pasted machine IDs run.'
      : choice.mode === 'group'
        ? `Devices added to the group later join ${joinUntil}.`
        : `Devices that match later, newly enrolled ones too, join ${joinUntil}.`;
  const [customDeadline, setCustomDeadline] = useState('');
  const [tryFirst, setTryFirst] = useState(false);
  // The deadline the confirm dialog shows; null = dialog closed.
  const [confirm, setConfirm] = useState<{ deadlineMs: number | null } | null>(null);
  const savedWidth = useUIStore((s) => s.bulkShellDrawerWidth);
  const saveWidth = useUIStore((s) => s.setBulkShellDrawerWidth);
  // While the edge is being dragged; saved (and persisted) once, on release.
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const width = dragWidth ?? savedWidth;

  // Tenant: a super admin picks one; with exactly one active tenant it is
  // picked for them. A tenant from "Run again" wins.
  const tenants = tenantsQuery.data ?? [];
  const activeTenants = tenants.filter((t) => t.is_active);
  const autoTenant = activeTenants.length === 1 ? activeTenants[0].id : '';
  const effectiveTenant = isSuperAdmin ? tenantId || autoTenant : '';
  const tenantChoices = tenants.filter((t) => t.is_active || t.id === tenantId || activeTenants.length === 0);
  const tenantName = tenants.find((t) => t.id === effectiveTenant)?.name;

  const targetReq = useMemo<ShellJobTargetRequest | null>(() => {
    // A super admin's request always names its tenant.
    if (isSuperAdmin && !effectiveTenant) return null;
    return targetRequest(choice, isSuperAdmin ? { tenant_id: effectiveTenant } : {});
  }, [isSuperAdmin, effectiveTenant, choice]);

  const debouncedReq = useDebouncedValue(targetReq, 250);
  // The drawer stays mounted while closed; it checks devices only while open.
  const preview = useShellJobPreview(open ? debouncedReq : null);
  const data = targetReq ? preview.data : undefined;
  const stale = preview.isFetching || targetReq !== debouncedReq;
  const total = data?.total ?? 0;
  const online = data?.online ?? 0;
  const offline = (data?.offline ?? 0) + (data?.not_enrolled ?? 0);


  const deadlineMs = resolveDeadline(deadline, customDeadline, timezone, now);
  const deadlineOk = deadlineMs === null || !Number.isNaN(deadlineMs);
  const deadlineText = deadlineMs !== null && deadlineOk ? formatDeadline(deadlineMs, timezone, now) : undefined;
  const longRun = isLongRunning(command);
  const canRun = !!data && !stale && total > 0 && command.trim() !== '' && deadlineOk;

  const deadlineHint: ReactNode =
    deadline === 'none' ? (
      'No deadline. Offline devices run when they come back, until you cancel the run.'
    ) : deadlineText && noTimeout ? (
      <>
        Nothing is sent after <Strong>{deadlineText}</Strong>. Offline devices get the command when they come back, until then.
      </>
    ) : deadlineText ? (
      <>
        Nothing is sent after <Strong>{deadlineText}</Strong>. Offline devices run when they come back, until then. Devices already
        running finish.
      </>
    ) : deadline === 'custom' && customDeadline === '' ? (
      'Pick a date and time.'
    ) : null;

  let footer: ReactNode = null;
  if (data) {
    const on = <Strong>{online.toLocaleString()} online</Strong>;
    footer =
      deadline === 'none' ? (
        <>
          Runs on {on} {online === 1 ? 'device' : 'devices'} now.{offline > 0 && ` ${offline.toLocaleString()} offline run when they come back.`}
        </>
      ) : (
        <>
          {on} run now
          {offline > 0
            ? ` · ${offline.toLocaleString()} offline run when back${deadlineText ? `, until ${deadlineText}` : ''}.`
            : '.'}
        </>
      );
  }

  const openConfirm = () => {
    const at = resolveDeadline(deadline, customDeadline, timezone, Date.now());
    if (at !== null && Number.isNaN(at)) return; // the custom time slipped under a minute away
    void preview.refetch(); // the dialog repeats these counts: make them current
    createJob.reset();
    setConfirm({ deadlineMs: at });
  };

  const submit = () => {
    // Creating a run is not idempotent: no second send while one is in flight,
    // or sent and waiting for the page to change.
    if (createJob.isPending || createJob.isSuccess) return;
    if (!targetReq) return;
    // Presets count from now — when Run is pressed, not when they were picked.
    const at = resolveDeadline(deadline, customDeadline, timezone, Date.now());
    if (at !== null && Number.isNaN(at)) {
      setConfirm(null); // back to the form; the Deadline field shows why
      return;
    }
    createJob.mutate(
      {
        ...targetReq,
        command: command.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        timeout_ms: timeoutMs,
        ...(noTimeout ? { slot_release_ms: slotReleaseMs } : {}),
        concurrency,
        ...(at !== null ? { expires_at: new Date(at).toISOString() } : {}),
        ...(tryFirst ? TRY_FIRST : {}),
      },
      { onSuccess: (job) => navigate(`/bulk-shell/${job.id}`) },
    );
  };

  // The left edge drags. Listeners sit on window, so a drag keeps going when
  // the pointer outruns the thin handle.
  const startResize = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.preventDefault(); // no text selection while dragging
    let last = width;
    const move = (ev: PointerEvent) => {
      last = clampWidth(window.innerWidth - ev.clientX);
      setDragWidth(last);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      document.body.style.cursor = '';
      setDragWidth(null);
      saveWidth(last);
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      ModalProps={{ keepMounted: true }}
      // MUI already gives a temporary drawer's paper role="dialog" and aria-modal; this names it.
      // The theme paints every Drawer paper in the dark sidebar colours; this
      // form takes the page's paper colours so its controls stay readable.
      PaperProps={{
        'aria-labelledby': 'new-run-title',
        // The width goes in a variable, not sx: it changes on every pointer move. Phones get the full screen.
        style: { '--drawer-width': `${width}px` } as CSSProperties,
        sx: {
          width: { xs: '100%', sm: `min(var(--drawer-width), calc(100vw - ${PAGE_GUTTER}px))` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderLeft: 1,
          borderColor: 'divider',
        },
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Drawer width"
        aria-valuenow={width}
        aria-valuemin={MIN_WIDTH}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        data-dragging={dragWidth !== null || undefined}
        onPointerDown={startResize}
        onDoubleClick={() => saveWidth(BULK_SHELL_DRAWER_WIDTH)}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          saveWidth(clampWidth(width + (e.key === 'ArrowLeft' ? 40 : -40)));
        }}
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 6,
          zIndex: 1,
          cursor: 'col-resize',
          touchAction: 'none',
          outline: 'none',
          '&:hover, &:focus-visible, &[data-dragging]': { bgcolor: 'primary.main', opacity: 0.35 },
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography component="h2" id="new-run-title" sx={{ fontSize: 15, fontWeight: 700 }}>
            New run
          </Typography>
          <Button variant="outlined" aria-label="Close" onClick={onClose} sx={plainButtonSx}>
            ✕
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pt: 0.5, pb: 2.5 }}>
          {!!prefill.no_answer && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {plural(prefill.no_answer, 'device')} of these did not answer last time. They may have run the command already.
            </Alert>
          )}

          {isSuperAdmin && (
            <Section label="Tenant">
              {tenantsQuery.isLoading ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading tenants…</Typography>
              ) : tenantsQuery.isError ? (
                <Alert
                  severity="error"
                  action={
                    <Button size="small" onClick={() => void tenantsQuery.refetch()}>
                      Retry
                    </Button>
                  }
                >
                  Could not load tenants.
                </Alert>
              ) : !tenantId && autoTenant ? (
                <Typography sx={{ fontSize: 13 }}>{tenantName}</Typography>
              ) : (
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  sx={selectSx}
                  slotProps={{
                    select: {
                      displayEmpty: true,
                      IconComponent: ChevronIcon,
                      renderValue: (v) =>
                        v ? (
                          (tenants.find((t) => t.id === v)?.name ?? String(v))
                        ) : (
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            Pick a tenant
                          </Box>
                        ),
                    },
                    htmlInput: { 'aria-label': 'Tenant' },
                  }}
                >
                  {tenantChoices.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Section>
          )}

          <Section>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Devices</Typography>
              <Button variant="outlined" disabled={isSuperAdmin && !effectiveTenant} onClick={() => setPickerOpen(true)} sx={plainButtonSx}>
                Change…
              </Button>
            </Box>
            {targetReq === null ? (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {isSuperAdmin && !effectiveTenant ? 'Pick a tenant to see its devices.' : 'Choose the devices to run on.'}
              </Typography>
            ) : preview.isError ? (
              <Alert severity="error">{getAPIErrorMessage(preview.error, 'Could not check these devices.')}</Alert>
            ) : data ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 12, opacity: stale ? 0.6 : 1 }}>
                  <Strong>{plural(total, 'device')}</Strong>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {online.toLocaleString()} online · {offline.toLocaleString()} offline
                  </Box>
                </Box>
                <Typography sx={{ mt: 0.5, fontSize: 13 }}>{choiceLine(choice, { tenantName, groupName, lines: data.lines })}</Typography>
              </>
            ) : (
              <CircularProgress size={16} />
            )}
            <Typography sx={{ mt: '6px', fontSize: 12, color: 'text.secondary' }}>{joinHint}</Typography>
          </Section>

          <Section label="Command">
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 1.5,
                py: 1.25,
                borderRadius: '8px',
                bgcolor: TERMINAL.bg,
                '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-1px' },
              }}
            >
              <Box component="span" aria-hidden sx={{ color: TERMINAL.prompt, fontFamily: SHELL_MONO, fontSize: 13, lineHeight: 1.6 }}>
                $
              </Box>
              <InputBase
                multiline
                minRows={3}
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                placeholder="getprop ro.build.version.release"
                inputProps={{ 'aria-label': 'Command', spellCheck: false }}
                sx={{
                  flex: 1,
                  p: 0,
                  color: TERMINAL.text,
                  fontFamily: SHELL_MONO,
                  fontSize: 13,
                  lineHeight: 1.6,
                  '& textarea': { caretColor: TERMINAL.prompt },
                  '& textarea::placeholder': { color: TERMINAL.muted, opacity: 1 },
                }}
              />
            </Box>
            {isCommandRisky(command) && (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                <strong>{command.trim().split(/\s+/)[0]}</strong> can change or break devices. You confirm once more before it runs.
              </Alert>
            )}
            {longRun.match && (
              <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                {longRun.reason} runs until the timeout: {longRun.suggestion}.
              </Alert>
            )}
            <Box sx={{ mt: 1.5 }}>
              <SmallLabel id="new-run-description">Description (optional)</SmallLabel>
              <TextField
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this run is for, e.g. Update to 2.5.0 for Gym A"
                helperText={description.length > 400 ? `${description.length} / 500` : undefined}
                sx={inputSx}
                slotProps={{ htmlInput: { maxLength: 500, 'aria-labelledby': 'new-run-description' } }}
              />
            </Box>
          </Section>

          <Section>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <SmallLabel id="new-run-timeout">Timeout per device</SmallLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={timeoutMs}
                  onChange={(e) => setTimeoutMs(Number(e.target.value))}
                  sx={selectSx}
                  slotProps={{ select: { labelId: 'new-run-timeout', IconComponent: ChevronIcon } }}
                >
                  {RUN_TIMEOUT_OPTIONS.map((o) => (
                    <MenuItem key={o.ms} value={o.ms}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                {noTimeout && (
                  <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
                    Send and forget: the run does not wait for answers.
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <SmallLabel id="new-run-concurrency">Devices at a time</SmallLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={concurrency}
                  onChange={(e) => setConcurrency(Number(e.target.value))}
                  sx={selectSx}
                  slotProps={{ select: { labelId: 'new-run-concurrency', IconComponent: ChevronIcon } }}
                >
                  {CONCURRENCY_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </TextField>
                {online > 0 && (
                  <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
                    {online.toLocaleString()} online · {noTimeout ? allSentIn(online, concurrency, slotReleaseMs) : paceEstimate(online, concurrency)}
                  </Typography>
                )}
              </Box>
              {noTimeout && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <SmallLabel id="new-run-slot">Free slot after</SmallLabel>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                      select
                      size="small"
                      value={slotReleaseMs}
                      onChange={(e) => setSlotReleaseMs(Number(e.target.value))}
                      sx={{ ...selectSx, width: 'calc(50% - 6px)', flexShrink: 0 }}
                      slotProps={{ select: { labelId: 'new-run-slot', IconComponent: ChevronIcon } }}
                    >
                      {SLOT_RELEASE_OPTIONS.map((o) => (
                        <MenuItem key={o.ms} value={o.ms}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Sends to {concurrency} devices, waits {slotReleaseLabel(slotReleaseMs)}, then continues with the next {concurrency}.
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <SmallLabel id="new-run-deadline">Deadline</SmallLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value as DeadlineChoice)}
                  sx={selectSx}
                  slotProps={{ select: { labelId: 'new-run-deadline', IconComponent: ChevronIcon } }}
                >
                  {DEADLINE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                {deadline === 'custom' && (
                  <CustomDeadlinePicker
                    value={customDeadline}
                    onChange={setCustomDeadline}
                    invalid={customDeadline !== '' && !deadlineOk}
                    now={now}
                    timezone={timezone}
                  />
                )}
              </Box>
            </Box>
            {deadlineHint && <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>{deadlineHint}</Typography>}
            {/* Folded by default; native <details> gives the ▸ marker and the expanded state. */}
            <Box component="details" sx={{ mt: '14px' }}>
              <Box component="summary" sx={{ cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
                Rollout · {tryFirst ? 'Try 10% first' : 'All at once'}
              </Box>
              <RadioGroup aria-label="Rollout" value={tryFirst ? 'try' : 'once'} onChange={(e) => setTryFirst(e.target.value === 'try')} sx={{ mt: 1 }}>
                <FormControlLabel value="once" control={<Radio size="small" />} label="All at once" sx={radioSx} />
                <FormControlLabel value="try" control={<Radio size="small" />} label="Try 10% first, then wait for me" sx={radioSx} />
              </RadioGroup>
            </Box>
          </Section>
        </Box>

        {/* pb clears the app's fixed system line (build · timezone) in the bottom-right corner, which sits above drawers. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, pt: 1.5, pb: 4, borderTop: 1, borderColor: 'divider' }}>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, color: 'text.secondary' }}>{footer}</Typography>
          <Button variant="outlined" onClick={onClose} sx={plainButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!canRun} onClick={openConfirm} sx={primaryButtonSx}>
            Run on {plural(total, 'device')}
          </Button>
        </Box>
      </Box>

      {pickerOpen && (
        <ChooseDevicesDialog
          tenantId={isSuperAdmin ? effectiveTenant : undefined}
          tenantName={tenantName}
          showOwnership={isSuperAdmin}
          canPickGroup={viewConfigs}
          value={choice}
          onCancel={() => setPickerOpen(false)}
          onUse={(c) => {
            setChoice(c);
            setPickerOpen(false);
          }}
        />
      )}
      <ConfirmRunDialog
        open={confirm !== null}
        pending={createJob.isPending || createJob.isSuccess}
        error={createJob.isError ? getAPIErrorMessage(createJob.error, 'Could not start the run.') : undefined}
        onCancel={() => setConfirm(null)}
        onRun={submit}
        summary={{
          devices: total,
          online,
          offline,
          tenant: isSuperAdmin ? (tenantName ?? effectiveTenant) : undefined,
          command: command.trim(),
          description: description.trim() || undefined,
          timeout: RUN_TIMEOUT_OPTIONS.find((o) => o.ms === timeoutMs)?.label ?? `${timeoutMs / 1000} s`,
          concurrency,
          deadline: confirm?.deadlineMs != null ? formatDeadline(confirm.deadlineMs, timezone, now) : undefined,
          noTimeout,
          slotRelease: slotReleaseLabel(slotReleaseMs),
          joinUntil: choice.mode !== 'list' && confirm?.deadlineMs != null ? formatDeadline(confirm.deadlineMs, timezone, now) : undefined,
          overlap: data?.overlap,
          rollout: tryFirst ? 'Try 10% first, then wait for me' : undefined,
        }}
      />
    </Drawer>
  );
}

/**
 * The custom deadline: Ant Design's date and time picker. The value stays the
 * zoned "2026-10-12T09:00" string, read in the profile timezone (not the
 * browser's): the picker only shows and edits those wall-clock digits.
 */
function CustomDeadlinePicker({ value, onChange, invalid, now, timezone }: {
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  now: number;
  timezone: string;
}) {
  const mui = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: mui.palette.mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: { colorPrimary: mui.palette.primary.main, fontSize: 13, borderRadius: 6, fontFamily: mui.typography.fontFamily },
      }}
    >
      <DatePicker
        showTime={{ format: 'HH:mm' }}
        // "Now" can never be a deadline: it must be at least a minute ahead.
        showNow={false}
        format="ddd, MMM D, YYYY · HH:mm"
        placeholder="Pick a date and time"
        value={value ? dayjs(value) : null}
        onChange={(d) => onChange(d ? d.format('YYYY-MM-DDTHH:mm') : '')}
        disabledDate={(d) => !customDeadlineDayAllowed(d.format('YYYY-MM-DD'), now, timezone)}
        status={invalid ? 'error' : undefined}
        // Inside the drawer: a popup on <body> would sit under it and lose focus to it.
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
        style={{ width: '100%', marginTop: 6 }}
        aria-label="Custom deadline"
      />
      <Typography sx={{ mt: '4px', fontSize: 12, color: invalid ? 'error.main' : 'text.secondary' }}>
        {invalid ? 'Pick a time between 1 minute and 30 days from now.' : `In your timezone (${timezone}).`}
      </Typography>
    </ConfigProvider>
  );
}
